/**
 * SettingsPanel — bottom-right gear toggle + glassy panel.
 *
 * Currently exposes sound controls (per `docs/16` §7.3 — accessibility
 * settings reachable from any screen via a ♡ icon). Future PRs add
 * "reduce motion", "haptic feedback", profile name + DOB editing, the
 * data-export button (`docs/15`), etc.
 *
 * The toggle and the panel are independent fixed-positioned elements.
 * When the panel is open, a transparent backdrop catches outside-taps
 * and closes the panel without stealing focus from game content.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSoundSettings } from '../sounds/settings.js';
import { useConsentStore } from '../telemetry/consent.js';
import { emit } from '../telemetry/emit.js';
import { markFunnel } from '../telemetry/funnel.js';
import styles from './SettingsPanel.module.css';

type Category = 'ui' | 'events' | 'voice' | 'ambient' | 'upgrades';

const CATEGORY_LABELS: Readonly<Record<Category, string>> = {
  ui: 'тапы и кнопки',
  events: 'события',
  voice: 'голос Сай',
  ambient: 'фон',
  upgrades: 'апгрейды',
};

/** Unicode-иконки per category — pictogram + текст для быстрого скана. */
const CATEGORY_ICONS: Readonly<Record<Category, string>> = {
  ui: '◌', // тап-ринг
  events: '✦', // вспышка
  voice: '❀', // голос-цветок
  ambient: '⌬', // фрактал-фон
  upgrades: '✧', // искра-апгрейд
};

/** Tiny CSS-toggle styled like an iOS switch. */
function Switch({
  on,
  onChange,
  label,
}: {
  readonly on: boolean;
  readonly onChange: (next: boolean) => void;
  readonly label: string;
}): JSX.Element {
  const trackClass = on ? `${styles.switch ?? ''} ${styles.switchOn ?? ''}` : (styles.switch ?? '');
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={trackClass}
      onClick={() => {
        onChange(!on);
      }}
    >
      <span className={styles.switchThumb} aria-hidden />
    </button>
  );
}

export function SettingsPanel(): JSX.Element {
  const [open, setOpen] = useState(false);
  // UX analytics: track когда панель открыта чтобы эмитить duration.
  const openedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (open) {
      openedAtRef.current = Date.now();
      emit('panel.opened', { panel: 'settings' });
      markFunnel('first_settings_open');
    } else if (openedAtRef.current !== null) {
      emit('panel.closed', {
        panel: 'settings',
        duration_ms: Date.now() - openedAtRef.current,
      });
      openedAtRef.current = null;
    }
  }, [open]);

  const masterMuted = useSoundSettings((s) => s.masterMuted);
  const masterVolume = useSoundSettings((s) => s.masterVolume);
  const categoryMuted = useSoundSettings((s) => s.categoryMuted);
  const setMasterMuted = useSoundSettings((s) => s.setMasterMuted);
  const setMasterVolume = useSoundSettings((s) => s.setMasterVolume);
  const setCategoryMuted = useSoundSettings((s) => s.setCategoryMuted);

  // GDPR right to withdraw — игрок может изменить consent в любой момент.
  const consentStatus = useConsentStore((s) => s.analytics);
  const grantConsent = useConsentStore((s) => s.grant);
  const denyConsent = useConsentStore((s) => s.deny);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <button
        type="button"
        className={styles.toggle}
        aria-label={open ? 'Закрыть настройки' : 'Открыть настройки'}
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        title="Настройки"
      >
        ♡
      </button>

      {open && (
        <>
          <div className={styles.backdrop} onClick={close} aria-hidden />
          <div className={styles.panel} role="dialog" aria-label="Настройки">
            <h2 className={styles.heading}>настройки</h2>

            {/* Master volume — single slider always visible (intent: easy
                reach for the most common ask: "сделай тише/громче"). */}
            <div className={styles.section}>
              <div className={styles.sectionLabel}>громкость</div>
              <div className={styles.volumeRow}>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={masterVolume}
                  onChange={(e) => {
                    setMasterVolume(Number(e.target.value));
                  }}
                  className={styles.slider}
                  aria-label="Общая громкость"
                />
                <div className={styles.volumeValue}>
                  {Math.round(masterVolume * 100).toString()}%
                </div>
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>звук</span>
                <Switch
                  on={!masterMuted}
                  onChange={(next) => {
                    setMasterMuted(!next);
                  }}
                  label={masterMuted ? 'Включить звук' : 'Выключить звук'}
                />
              </div>
            </div>

            <hr className={styles.divider} />

            {/* Per-category mutes — for players who want only ambient or
                only the tap ding without the other layers. */}
            <div className={styles.section}>
              <div className={styles.sectionLabel}>по категориям</div>
              {(Object.keys(CATEGORY_LABELS) as Category[]).map((cat) => (
                <div key={cat} className={styles.row}>
                  <span className={styles.rowLabel}>
                    <span className={styles.rowIcon} aria-hidden>
                      {CATEGORY_ICONS[cat]}
                    </span>
                    {CATEGORY_LABELS[cat]}
                  </span>
                  <Switch
                    on={!categoryMuted[cat]}
                    onChange={(next) => {
                      setCategoryMuted(cat, !next);
                    }}
                    label={`Категория ${CATEGORY_LABELS[cat]}`}
                  />
                </div>
              ))}
            </div>

            <hr className={styles.divider} />

            {/* GDPR right to withdraw — consent control в любой момент.
                Doc 15 + task #51. */}
            <div className={styles.section}>
              <div className={styles.sectionLabel}>аналитика</div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>
                  делиться шагами с Сай
                  <span className={styles.rowHint}>
                    {consentStatus === 'granted' && 'сейчас: делишься'}
                    {consentStatus === 'denied' && 'сейчас: нет'}
                    {consentStatus === 'unknown' && 'ещё не решено'}
                  </span>
                </span>
                <Switch
                  on={consentStatus === 'granted'}
                  onChange={(next) => {
                    if (next) grantConsent();
                    else denyConsent();
                  }}
                  label="Согласие на аналитику"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
